import React from 'react';
import { Navbar } from '../components/Navbar';
import { HeroSection } from '../components/HeroSection';
import { LiveShiftBoard } from '../components/LiveShiftBoard';
import { SavingsEstimator } from '../components/SavingsEstimator';
import { TrustSection } from '../components/TrustSection';
import { StickyFooter } from '../components/StickyFooter';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { ServicesSection } from '../components/ServicesSection';
export function Home() {
  return <div className="min-h-screen w-full bg-white" data-id="element-962">
      {/* Navigation */}
      <Navbar data-id="element-963" />

      {/* Main content */}
      <main data-id="element-964">
        {/* Hero: compact mobile-first with multi-step widget */}
        <HeroSection data-id="element-965" />

        {/* Dual-navigation services: Hire Now grid + Care Solutions menu */}
        <ServicesSection data-id="element-966" />

        {/* How it works: quick 3-step explainer */}
        <HowItWorks data-id="element-967" />

        {/* Live shift availability board */}
        <LiveShiftBoard data-id="element-968" />

        {/* Savings estimator — immediately after marketplace feed */}
        <SavingsEstimator data-id="element-969" />

        {/* Trust: Proudly Canadian & Nurse-Owned */}
        <TrustSection data-id="element-970" />

        {/* FAQ section */}
        <FAQSection data-id="element-971" />
      </main>

      {/* Footer with sticky app download CTAs */}
      <StickyFooter data-id="element-972" />

      {/* Mobile bottom navigation */}
      <MobileBottomNav data-id="element-973" />

      {/* Spacer for sticky footer on desktop */}
      <div className="hidden md:block h-14" aria-hidden="true" data-id="element-974" />
      {/* Spacer for mobile bottom nav */}
      <div className="md:hidden h-16" aria-hidden="true" data-id="element-975" />
    </div>;
}
function HowItWorks() {
  return <section className="w-full bg-white py-16 border-b border-gray-100" aria-label="How Care Nomad works" data-id="element-976">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-977">
        <div className="text-center mb-12" data-id="element-978">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-3" data-id="element-979">
            Staffed in <span className="text-medical-blue" data-id="element-980">Minutes</span>, Not
            Days
          </h2>
          <p className="text-text-muted text-lg" data-id="element-981">
            Three simple steps to fill any shift
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-982">
          {/* Connector line (desktop) */}
          <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-0.5 bg-soft-teal" aria-hidden="true" data-id="element-983" />

          {/* Step 1 */}
          <div className="text-center" data-id="element-984">
            <div className="w-20 h-20 bg-soft-teal rounded-2xl flex items-center justify-center mx-auto mb-5 relative" data-id="element-985">
              <span className="text-3xl font-extrabold text-medical-blue" data-id="element-986">
                1
              </span>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-medical-blue rounded-full flex items-center justify-center" data-id="element-987">
                <span className="text-white text-xs font-bold" data-id="element-988">✓</span>
              </div>
            </div>
            <h3 className="font-bold text-text-dark text-lg mb-2" data-id="element-989">
              Post Your Shift
            </h3>
            <p className="text-text-muted text-sm leading-relaxed" data-id="element-990">
              Enter your postal code, required role (PSW, RPN, RN), shift time,
              and any special requirements. Takes under 60 seconds.
            </p>
          </div>

          {/* Step 2 */}
          <div className="text-center" data-id="element-991">
            <div className="w-20 h-20 bg-soft-teal rounded-2xl flex items-center justify-center mx-auto mb-5 relative" data-id="element-992">
              <span className="text-3xl font-extrabold text-medical-blue" data-id="element-993">
                2
              </span>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent-teal rounded-full flex items-center justify-center" data-id="element-994">
                <span className="text-white text-xs" data-id="element-995">⚡</span>
              </div>
            </div>
            <h3 className="font-bold text-text-dark text-lg mb-2" data-id="element-996">
              Get Matched Instantly
            </h3>
            <p className="text-text-muted text-sm leading-relaxed" data-id="element-997">
              Our algorithm matches you with nurse-vetted workers nearby. Review
              profiles, ratings, and credentials before confirming.
            </p>
          </div>

          {/* Step 3 */}
          <div className="text-center" data-id="element-998">
            <div className="w-20 h-20 bg-soft-teal rounded-2xl flex items-center justify-center mx-auto mb-5 relative" data-id="element-999">
              <span className="text-3xl font-extrabold text-medical-blue" data-id="element-1000">
                3
              </span>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent-teal rounded-full flex items-center justify-center" data-id="element-1001">
                <span className="text-white text-xs" data-id="element-1002">🍁</span>
              </div>
            </div>
            <h3 className="font-bold text-text-dark text-lg mb-2" data-id="element-1003">
              Shift Covered
            </h3>
            <p className="text-text-muted text-sm leading-relaxed" data-id="element-1004">
              Worker arrives, shift is tracked in-app, and payment is processed
              automatically. Rate and review after each shift.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
type FAQItem = {
  question: string;
  answer: string;
};
const FAQ_ITEMS: FAQItem[] = [{
  question: 'How quickly can I get a worker placed?',
  answer: 'Our average match time is 4 minutes. For urgent shifts, we have workers available 24/7. Most facilities have their shift covered within 15 minutes of posting.'
}, {
  question: 'How are workers vetted?',
  answer: 'Every worker is screened by a registered nurse on our team. We verify CNO/CARNA/BCCNM registration, check references, conduct clinical competency assessments, and run criminal background checks.'
}, {
  question: 'What roles are available on Care Nomad?',
  answer: 'We currently offer PSWs (Personal Support Workers), RPNs (Registered Practical Nurses), RNs (Registered Nurses), LPNs (Licensed Practical Nurses), and HCAs (Health Care Aides).'
}, {
  question: 'Is Care Nomad available across Canada?',
  answer: "We currently serve Ontario, British Columbia, and Alberta. We're expanding to additional provinces in 2025. Enter your postal code to check availability in your area."
}, {
  question: 'How does billing work?',
  answer: "Billing is fully automated and transparent. You'll see the exact rate before confirming any shift. Invoices are generated automatically and payment is processed within 2 business days of shift completion."
}];
function FAQSection() {
  return <section className="w-full bg-white py-16 lg:py-24" aria-label="Frequently asked questions" data-id="element-1005">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1006">
        <div className="text-center mb-12" data-id="element-1007">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-3" data-id="element-1008">
            Frequently Asked Questions
          </h2>
          <p className="text-text-muted text-lg" data-id="element-1009">
            Everything you need to know about Care Nomad
          </p>
        </div>

        <div className="space-y-4" data-id="element-1010">
          <FAQAccordion items={FAQ_ITEMS} data-id="element-1011" />
        </div>

        <div className="mt-10 text-center bg-soft-teal rounded-2xl p-8" data-id="element-1012">
          <h3 className="font-bold text-text-dark text-xl mb-2" data-id="element-1013">
            Still have questions?
          </h3>
          <p className="text-text-muted text-sm mb-5" data-id="element-1014">
            Our team is available 24/7 to help you get started
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1015">
            <a href="tel:+18005551234" className="bg-medical-blue text-white font-bold px-6 py-3 rounded-xl hover:bg-medical-blue-dark transition-colors text-sm" data-id="element-1016">
              📞 Call 1-800-555-1234
            </a>
            <a href="#" className="bg-white text-medical-blue font-bold px-6 py-3 rounded-xl border-2 border-medical-blue hover:bg-soft-teal transition-colors text-sm" data-id="element-1017">
              💬 Live Chat
            </a>
          </div>
        </div>
      </div>
    </section>;
}
function FAQAccordion({
  items
}: {
  items: FAQItem[];
}) {
  return <>
      {items.map((item, index) => <FAQItem key={index} item={item} data-id="element-1018" />)}
    </>;
}
function FAQItem({
  item
}: {
  item: FAQItem;
}) {
  return <details className="group bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-1019">
      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none hover:bg-soft-teal transition-colors" data-id="element-1020">
        <span className="font-semibold text-text-dark text-sm pr-4" data-id="element-1021">
          {item.question}
        </span>
        <span className="text-medical-blue text-xl font-light flex-shrink-0 group-open:rotate-45 transition-transform duration-200" data-id="element-1022">
          +
        </span>
      </summary>
      <div className="px-6 pb-5 pt-1" data-id="element-1023">
        <p className="text-text-muted text-sm leading-relaxed" data-id="element-1024">{item.answer}</p>
      </div>
    </details>;
}